import {
  Video,
  ArrowRight,
  Sparkles,
  DatabaseIcon,
  Eye,
  Shield,
  Target,
  TrendingUp,
  MessageSquare,
  Brain,
  Instagram,
  Linkedin,
  Youtube,
} from "lucide-react";
import { DynamicLucideIcon } from "@/components/DynamicLucideIcon";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Header } from "@/components/layout/Header";
import { WhoItsForSection } from "@/components/WhoItsForSection";
import { useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchMeetingCollections } from "@/store/slices/meetingSlice";
import { getMeetingModules } from "@/store/slices/meetingModulesSlice";
import { selectTotalModulesCountRounded } from "@/store/selectors/meetingModulesSelectors";
import { generateCategoryId } from "@/utils/categoryId";
import { logger } from "@/utils/logger";
import { useProduct } from "@/hooks/useProduct";
import { cn } from "@/lib/utils";
import { SEOPresets } from "@/components/SEO";
import { SentryTestButton } from "@/components/SentryTestButton";

const Index = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const config = useProduct();

  // Seleciona collections do Redux
  const { collections, collectionsLoading, collectionsError } = useAppSelector(
    (state) => state.meeting
  );

  // Seleciona contagem total de módulos arredondada
  const totalModulesCount = useAppSelector(selectTotalModulesCountRounded);

  // Scroll to top when page mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  }, []);

  // Busca collections e modules ao montar (com cache)
  useEffect(() => {
    logger.log("[Index] Mounting - fetching collections and modules");
    dispatch(fetchMeetingCollections(false)); // false = usa cache se disponível
    dispatch(getMeetingModules({})); // Busca módulos para contagem
  }, [dispatch]);

  // Debug logs
  useEffect(() => {
    logger.log("[Index] Collections state:", {
      loading: collectionsLoading,
      error: collectionsError,
      count: collections.length,
      collections: collections,
    });
  }, [collectionsLoading, collectionsError, collections]);
  const benefits = [
    "Practice in a safe, judgment-free environment",
    "Get instant AI-powered feedback",
    "Build confidence before real conversations",
    totalModulesCount > 0
      ? `Access ${totalModulesCount}+ realistic scenarios`
      : "Access realistic scenarios",
  ];
  const features = [
    {
      icon: Video,
      title: "AI Conversation Simulator",
      description:
        "Master professional communication with realistic conversation simulations tailored for software developers.",
      features: [
        "Daily standups, retrospectives, 1:1s",
        "Performance reviews & career discussions",
        "Technical syncs & code reviews",
        "Incident response & post-mortems",
      ],
      cta: "Start practicing meetings",
      ctaLink: "/meeting-simulator",
      gradient: "from-primary to-accent",
    },
  ];
  return (
    <>
      {SEOPresets.home}
      <Header showLogout solidBackground={true} />

      {import.meta.env.DEV && (
        <div className="absolute top-20 right-4 z-50 bg-background/95 backdrop-blur shadow-lg rounded-lg border">
          <SentryTestButton />
        </div>
      )}

      {/* Hero Section with Tech-Inspired Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary via-accent to-primary py-24 px-4 sm:px-6 lg:px-8">
        {/* Grid pattern background */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />

        {/* Animated gradient orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-pulse"
            style={{
              animationDelay: "1s",
            }}
          />
        </div>

        {/* Code-like decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top left code snippet */}
          <div className="absolute top-12 left-8 text-white/20 font-mono text-sm animate-fade-in hidden lg:block">
            <div className="space-y-1">
              <div>{"{ practice: true,"}</div>
              <div className="pl-4">{"confidence: 100%,"}</div>
              <div className="pl-4">{'skills: "improved" }'}</div>
            </div>
          </div>

          {/* Top right terminal-like element */}
          <div
            className="absolute top-20 right-12 text-white/15 font-mono text-xs animate-fade-in hidden lg:block"
            style={{
              animationDelay: "0.5s",
            }}
          >
            <div className="border border-white/20 rounded-lg p-3 backdrop-blur-sm bg-white/5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-red-400/60" />
                <div className="w-3 h-3 rounded-full bg-yellow-400/60" />
                <div className="w-3 h-3 rounded-full bg-green-400/60" />
              </div>
              <div>$ npm run practice</div>
              <div className="text-green-400/60">✓ Session started</div>
            </div>
          </div>

          {/* Bottom left floating code brackets */}
          <div
            className="absolute bottom-32 left-16 text-white/10 font-mono text-6xl animate-pulse hidden lg:block"
            style={{
              animationDelay: "1s",
            }}
          >
            {"< />"}
          </div>

          {/* Bottom right API-like element */}
          <div
            className="absolute bottom-24 right-20 text-white/15 font-mono text-xs animate-fade-in hidden lg:block"
            style={{
              animationDelay: "1.5s",
            }}
          >
            <div className="space-y-1">
              <div>{"GET /api/improve"}</div>
              <div className="text-green-400/60">{"→ 200 OK"}</div>
            </div>
          </div>
        </div>

        {/* Floating tech icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute top-1/4 left-1/4 w-8 h-8 border-2 border-white/20 rounded-lg animate-float"
            style={{
              animationDuration: "6s",
              animationDelay: "0s",
            }}
          >
            <div className="w-full h-full flex items-center justify-center text-white/30 text-xs font-mono">
              fn
            </div>
          </div>
          <div
            className="absolute top-1/3 right-1/4 w-6 h-6 border-2 border-white/15 rotate-45 animate-float"
            style={{
              animationDuration: "8s",
              animationDelay: "1s",
            }}
          />
          <div
            className="absolute bottom-1/3 left-1/3 w-10 h-10 border-2 border-white/20 rounded-full animate-float"
            style={{
              animationDuration: "7s",
              animationDelay: "0.5s",
            }}
          >
            <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">
              {}
            </div>
          </div>
          <div
            className="absolute top-2/3 right-1/3 w-7 h-7 border border-white/20 animate-float"
            style={{
              animationDuration: "9s",
              animationDelay: "2s",
            }}
          >
            <div className="w-full h-full flex items-center justify-center text-white/30 text-xs font-mono">
              {"=> "}
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center mb-12 animate-fade-in">
            <div className="relative"></div>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white mb-12 tracking-tight animate-fade-in leading-tight">
            {config.i18n.hero.title}
            <br />
            <span className="text-4xl sm:text-5xl lg:text-6xl font-[Outfit,sans-serif]">
              {" "}
              {config.i18n.hero.subtitle}
            </span>
          </h1>

          <p className="text-xl sm:text-2xl text-white/95 max-w-4xl mx-auto leading-relaxed mb-16 animate-fade-in font-medium">
            {config.i18n.hero.description}
          </p>

          <div className="flex justify-center items-center animate-fade-in mb-20">
            <Button
              size="lg"
              onClick={() => navigate("/meeting-simulator")}
              className="bg-white text-primary hover:bg-white hover:scale-105 text-lg px-10 py-7 h-auto font-bold shadow-2xl transition-transform duration-300"
            >
              {config.i18n.hero.ctaButton}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          {/* Stats Section */}
        </div>
      </div>

      {/* Meeting Categories Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div
          id="meeting-categories"
          className="text-center mb-16 animate-fade-in scroll-mt-24"
        >
          {/* <div className="inline-block mb-4">
            <span className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-bold">
              {config.i18n.categories.badge}
            </span>
          </div> */}
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
            {config.i18n.categories.title}
          </h2>
          <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {totalModulesCount > 0
              ? config.i18n.categories.subtitle
              : config.i18n.categories.subtitleFallback}
          </p>
        </div>

        {/* Loading state */}
        {collectionsLoading && (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-muted-foreground text-lg">
              {config.i18n.loading}
            </p>
          </div>
        )}

        {/* Error state */}
        {collectionsError && (
          <div className="text-center py-20">
            <div className="bg-destructive/10 text-destructive px-6 py-4 rounded-lg max-w-md mx-auto">
              <p className="font-semibold mb-2">
                {config.i18n.failedToLoadCategories}
              </p>
              <p className="text-sm">{collectionsError}</p>
              <Button
                onClick={() => dispatch(fetchMeetingCollections(true))}
                className="mt-4"
                variant="outline"
              >
                {config.i18n.tryAgain}
              </Button>
            </div>
          </div>
        )}

        {/* Collections grid */}
        {!collectionsLoading && !collectionsError && collections.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
            {[...collections]
              .sort((a, b) => a.order - b.order)
              .map((collection) => {
                // Use utility function to generate consistent category ID
                const categoryId = generateCategoryId(collection.name);
                return (
                  <Card
                    key={collection.id}
                    className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-border/50 hover:border-primary/50 bg-card/80 backdrop-blur cursor-pointer overflow-hidden relative"
                    onClick={() => navigate(`/meeting-simulator#${categoryId}`)}
                  >
                    <div
                      className={cn(
                        "absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity",
                        collection.color
                      )}
                    />
                    <CardContent className="p-8 relative z-10">
                      <div
                        className={cn(
                          "w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300",
                          collection.color
                        )}
                      >
                        <DynamicLucideIcon
                          name={collection.icon}
                          className="w-8 h-8 text-white"
                          fallback="Target"
                        />
                      </div>
                      <h3 className="font-bold text-xl text-foreground mb-3">
                        {collection.name}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {collection.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        )}

        {/* Empty state */}
        {!collectionsLoading &&
          !collectionsError &&
          collections.length === 0 && (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">
                {config.i18n.noConversationCategories}
              </p>
            </div>
          )}

        {/* Services Section with Enhanced Design */}
        <div className="max-w-3xl mx-auto animate-fade-in"></div>
      </div>

      {/* Who It's For Section */}
      <WhoItsForSection />

      {/* Data Protection Section */}
      <div className="bg-gradient-to-br from-muted/50 via-background to-muted/30 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            {/* <div className="inline-block mb-4">
              <span className="bg-green-500/10 text-green-600 dark:text-green-400 px-4 py-2 rounded-full text-sm font-bold">
                {config.i18n.dataProtection.badge}
              </span>
            </div> */}
            <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-4">
              {config.i18n.dataProtection.title}
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {config.i18n.dataProtection.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: DatabaseIcon,
                title: config.i18n.dataProtection.encryption.title,
                description: config.i18n.dataProtection.encryption.description,
                color: "from-blue-500 to-cyan-600",
              },
              {
                icon: Eye,
                title: config.i18n.dataProtection.privacy.title,
                description: config.i18n.dataProtection.privacy.description,
                color: "from-purple-500 to-pink-600",
              },
              {
                icon: Shield,
                title: config.i18n.dataProtection.storage.title,
                description: config.i18n.dataProtection.storage.description,
                color: "from-green-500 to-emerald-600",
              },
            ].map((item, index) => (
              <Card
                key={index}
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-border/50 hover:border-green-500/50 bg-card/80 backdrop-blur"
              >
                <CardContent className="p-8 text-center">
                  <div
                    className={`w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}
                  >
                    <item.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Card className="max-w-2xl mx-auto border-2 border-green-500/20 bg-green-500/5">
              <CardContent className="p-6">
                <p className="text-foreground font-medium">
                  <MessageSquare className="w-5 h-5 inline-block mr-2 text-green-600 dark:text-green-400" />
                  {
                    config.i18n.dataProtection.bottomMessage.split(
                      "only you have access to it"
                    )[0]
                  }
                  <span className="font-bold text-green-600 dark:text-green-400">
                    only you have access to it
                  </span>
                  {
                    config.i18n.dataProtection.bottomMessage.split(
                      "only you have access to it"
                    )[1]
                  }
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-background py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-16">
            {/* <div className="inline-block mb-4">
              <span className="bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-bold">
                {config.i18n.howItWorks.badge}
              </span>
            </div> */}
            <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-4">
              {config.i18n.howItWorks.title}
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {config.i18n.howItWorks.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connection lines */}
            <div className="hidden md:block absolute top-16 left-1/3 right-1/3 h-1 bg-gradient-to-r from-primary via-accent to-primary" />

            {[
              {
                step: "1",
                icon: Target,
                title: config.i18n.howItWorks.step1.title,
                description: config.i18n.howItWorks.step1.description,
                color: "from-blue-500 to-cyan-600",
              },
              {
                step: "2",
                icon: Brain,
                title: config.i18n.howItWorks.step2.title,
                description: config.i18n.howItWorks.step2.description,
                color: "from-purple-500 to-pink-600",
              },
              {
                step: "3",
                icon: TrendingUp,
                title: config.i18n.howItWorks.step3.title,
                description: config.i18n.howItWorks.step3.description,
                color: "from-orange-500 to-red-600",
              },
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="relative inline-block mb-6">
                  <div
                    className={`w-32 h-32 rounded-3xl bg-gradient-to-br ${item.color} text-white text-4xl font-extrabold flex items-center justify-center mx-auto shadow-2xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 relative z-10`}
                  >
                    <item.icon className="w-16 h-16 text-white" />
                  </div>
                  <div className="absolute top-0 left-0 w-12 h-12 bg-white dark:bg-background rounded-full flex items-center justify-center shadow-lg -translate-x-2 -translate-y-2 border-4 border-background z-20">
                    <span className="text-2xl font-bold text-foreground">
                      {item.step}
                    </span>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-3">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="relative bg-gradient-to-br from-primary via-accent to-primary rounded-3xl p-12 sm:p-16 text-center text-white overflow-hidden shadow-2xl">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
            <div
              className="absolute bottom-0 right-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-pulse"
              style={{
                animationDelay: "1s",
              }}
            />
          </div>

          <div className="relative z-10">
            <div className="inline-block mb-6">
              <Sparkles className="w-16 h-16 mx-auto animate-pulse" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-6">
              {config.i18n.cta.title}
            </h2>
            <p className="text-2xl text-white/95 mb-10 max-w-3xl mx-auto font-medium">
              {config.i18n.cta.subtitle}
            </p>
            <div className="flex justify-center">
              <Button
                size="lg"
                onClick={() => navigate("/meeting-simulator")}
                className="bg-white text-primary hover:bg-white hover:scale-105 text-xl px-12 py-8 h-auto font-bold shadow-2xl transition-transform"
              >
                {config.i18n.cta.button}
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Footer */}
      <footer className="relative bg-gradient-to-br from-primary via-accent to-primary mt-20">
        {/* Decorative grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Main footer content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* About section */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                {config.i18n.footer.companyName}
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                {config.i18n.footer.companyDescription}
              </p>
            </div>

            {/* Quick Links */}

            {/* Resources */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                {config.i18n.footer.resources}
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/faq"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    {config.i18n.footer.faq}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    {config.i18n.footer.privacyPolicy}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-of-service"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    {config.i18n.footer.termsOfService}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/subscription-billing"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    {config.i18n.footer.subscriptionBilling}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact & Social */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                {config.i18n.footer.contact}
              </h3>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                {config.i18n.footer.contactDescription}
              </p>
              {/* Social Media Links */}
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/globalcoders.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                  className="text-white/70 hover:text-white transition-colors duration-200"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/globalcoders/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on LinkedIn"
                  className="text-white/70 hover:text-white transition-colors duration-200"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://youtube.com/@globalcodersenglish"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Subscribe on YouTube"
                  className="text-white/70 hover:text-white transition-colors duration-200"
                >
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Funding section */}
          <div className="border-t border-white/10 pt-8 pb-8">
            <div className="flex justify-center items-center mb-8">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-3 shadow-2xl">
                <img
                  src={
                    new URL("../assets/footer-funding.jpeg", import.meta.url)
                      .href
                  }
                  alt={config.i18n.footer.fundingAlt}
                  className="max-w-full h-auto"
                  style={{
                    maxHeight: "140px",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/60 text-sm">
                {config.i18n.footer.copyright(new Date().getFullYear())}
              </p>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <span>{config.i18n.footer.madeWith}</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};
export default Index;
